// TrainingProfileSection — editovatelný tréninkový profil klienta (PG-only).
// 29.5.2026: nahrazuje read-only sekci ze Sheetu. Trenér + management editují.
//
// Struktura odpovídá šabloně kterou používá tým:
//   - Základní info (4 textareas: doba, omezení, zranění, bolesti)
//   - Informace k tréninku (4 textareas: dlouhodobý cíl, aktuální cíl, omezení, nezvládne)
//   - Cviky (tabulka: cvik / váha / poznámka) - přidat/smazat/edit řádek
//   - Kardio (tabulka: cvik / info / poznámka)
//   - Poslední tréninky (volný text, číslovaný seznam)
//   - Poznámky (volný text)
//   - Pro náhrady (volný text)
//
// UX:
//   - Default view mode (read-only zobrazení).
//   - "Upravit" toggle - editace inline, autosave per field on blur.
//   - Mobilní layout: vše stack pod sebou, větší tap targety.

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { Plus, Trash2, Check, X, Loader2, Search, Sparkles, ChevronRight, ChevronDown, Dumbbell, Heart, AlertTriangle, Target, CalendarDays, BookOpen, Users } from "lucide-react";
import { apiFetch } from "../lib/api.js";
import { searchCviky, CVIKY_DATABAZE } from "../lib/cviky_databaze.js";
import TrainingSessionsLog from "./TrainingSessionsLog.jsx";
import { formatDateTime } from "../lib/format.js";
import { toast } from "./Toast.jsx";
import { Oddelovac } from "./ui.jsx";

// 29.5.2026 v13: localStorage cache pro instant render při znovuotevření detailu klienta.
// Trenéři detail klientů otevírají často mezi 8-20h, čekání 500ms na PG fetch je rušivé.
// Cache strategie: zobraz cache okamžitě (1h TTL), pak background refresh.
const CACHE_PREFIX = "fw40_ctp_";
const CACHE_TTL_MS = 60 * 60 * 1000; // 1h

function readCache(klientId) {
  if (!klientId || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CACHE_PREFIX + klientId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || Date.now() - (parsed._ts || 0) > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch { return null; }
}
function writeCache(klientId, data) {
  if (!klientId || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CACHE_PREFIX + klientId, JSON.stringify({ _ts: Date.now(), data }));
  } catch { /* quota / private mode */ }
}

export default function TrainingProfileSection({ klientName, canEdit = false }) {
  const klientId = String(klientName || "").toLowerCase().trim();
  // Inicializovat z cache - instant render pokud máme čerstvá data
  const initialCached = readCache(klientId);
  const [data, setData] = useState(initialCached);
  const [loading, setLoading] = useState(!initialCached);
  const [error, setError] = useState(null);
  const [savingField, setSavingField] = useState(null);
  // 9.9.2026 — zelene "Ulozeno" na dve vteriny po ulozeni a pocet zapisu z tréninků,
  // ktery si hlasi TrainingSessionsLog, aby sel ukazat v hlavicce podsekce.
  const [savedField, setSavedField] = useState(null);
  const [zapisyPocet, setZapisyPocet] = useState(null);

  const load = useCallback(async () => {
    if (!klientId) return;
    // Pokud nemáme cache, ukaž loading. Jinak silent refresh na pozadí.
    if (!readCache(klientId)) setLoading(true);
    setError(null);
    try {
      const r = await apiFetch(`/api/client-training-profile?klient_id=${encodeURIComponent(klientId)}`, { credentials: "include" });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Načtení selhalo");
      setData(j.profile);
      writeCache(klientId, j.profile);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [klientId]);

  useEffect(() => { load(); }, [load]);

  // Save jednoho pole. Optimistic - lokálně přepíšeme hned, API běží na pozadí.
  const saveField = useCallback(async (col, value) => {
    if (!klientId) return;
    const prev = data;
    setData((d) => ({ ...d, [col]: value }));
    setSavingField(col);
    try {
      const r = await apiFetch("/api/client-training-profile", {
        method: "POST",
        body: JSON.stringify({ klient_id: klientId, fields: { [col]: value, klient_name: klientName } }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Uložení selhalo");
      if (j.profile) { setData(j.profile); writeCache(klientId, j.profile); }
      setSavedField(col);
      setTimeout(() => setSavedField((f) => (f === col ? null : f)), 2000);
    } catch (e) {
      setData(prev);
      toast(`Chyba uložení (${col}): ${e.message}`, "error");
    } finally {
      setSavingField(null);
    }
  }, [klientId, klientName, data]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 italic p-4">
        <Loader2 className="w-4 h-4 animate-spin" /> Načítám tréninkový profil…
      </div>
    );
  }
  if (error) {
    return <div className="text-sm text-rose-700 p-4">Chyba: {error}</div>;
  }
  if (!data) return null;

  const hasAny =
    data.zaklad_vyhovuje_doba || data.zaklad_omezeni || data.zaklad_zraneni || data.zaklad_bolesti ||
    data.trenink_dlouhodoby_cil || data.trenink_aktualni_cil || data.trenink_omezeni || data.trenink_nezvladne ||
    (data.cviky && data.cviky.length > 0) || (data.kardio && data.kardio.length > 0) ||
    data.posledni_treninky || data.poznamky || data.pro_nahrady;

  // 29.5.2026 v10: Žádný globální "Upravit" toggle - vše inline editovatelné pokud `canEdit`.
  // Trenér klikne přímo do pole nebo na "Přidat cvik" a píše. Save on blur per pole.
  // Audit footer je jen drobný indikátor (uložení / poslední úprava), žádné tlačítko nahoře.
  const editing = canEdit;

  // 9.9.2026 (redesign) — profil je sedm rozbalovacích podsekcí, stejně jako sekce
  // v detailu klienta. Vedle názvu je počet nebo "nevyplněno", ať je bez rolování vidět,
  // kde něco je. Co si trenér rozbalí, to mu tam zůstane. Data ani ukládání se nemění.
  const kolikVyplnenych = (...vals) => vals.filter((v) => String(v || "").trim()).length;
  const zdraviPocet = kolikVyplnenych(data.zaklad_omezeni, data.zaklad_bolesti, data.zaklad_zraneni, data.trenink_nezvladne);
  const cilePocet = kolikVyplnenych(data.trenink_dlouhodoby_cil, data.trenink_aktualni_cil, data.zaklad_vyhovuje_doba, data.trenink_omezeni);
  const cvikyPocet = (data.cviky || []).filter((r) => r && String(r.cvik || "").trim()).length;
  const kardioPocet = (data.kardio || []).filter((r) => r && String(r.cvik || "").trim()).length;

  return (
    <div className="space-y-2">
      {canEdit && (savingField || savedField || data.updated_at) && (
        <div className="-mt-1 px-0.5 text-xs text-slate-500">
          {savingField ? (
            <span className="inline-flex items-center gap-1.5 font-medium text-brand-700">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Ukládám…
            </span>
          ) : savedField ? (
            <span className="inline-flex items-center gap-1.5 font-medium text-emerald-700">
              <Check className="h-3.5 w-3.5" /> Uloženo
            </span>
          ) : (
            <span>Naposledy upraveno {formatDateTime(data.updated_at)}</span>
          )}
        </div>
      )}

      <Podsekce
        id="zdravi"
        icon={AlertTriangle}
        danger
        title="Zdraví a omezení"
        badge={zdraviPocet ? <PocetOdznak tone="rose">{zdraviPocet}</PocetOdznak> : <PrazdnyOdznak>nevyplněno</PrazdnyOdznak>}
      >
        <TextField label="Zdravotní omezení" value={data.zaklad_omezeni || ""} editing={editing} multiline placeholder="Štítná žláza, vysoký tlak, alergie, hormonální výkyvy…" onSave={(v) => saveField("zaklad_omezeni", v)} />
        <TextField label="Chronické bolesti" value={data.zaklad_bolesti || ""} editing={editing} multiline placeholder="Záda, kyčle, ramena…" onSave={(v) => saveField("zaklad_bolesti", v)} />
        <TextField label="Zranění" value={data.zaklad_zraneni || ""} editing={editing} multiline placeholder="Aktuální i staré (operace, výrony, zlomeniny)" onSave={(v) => saveField("zaklad_zraneni", v)} />
        <TextField label="Jaké cviky nezvládne" value={data.trenink_nezvladne || ""} editing={editing} multiline placeholder="Konkrétní cviky, varianty" onSave={(v) => saveField("trenink_nezvladne", v)} />
      </Podsekce>

      <Podsekce
        id="cile"
        icon={Target}
        title="Cíle a jak trénuje"
        badge={cilePocet ? <PocetOdznak>{cilePocet}</PocetOdznak> : <PrazdnyOdznak>nevyplněno</PrazdnyOdznak>}
      >
        <TextField label="Dlouhodobý cíl" value={data.trenink_dlouhodoby_cil || ""} editing={editing} multiline placeholder="Restart pohybu, kondice, redukce, hypertrofie…" onSave={(v) => saveField("trenink_dlouhodoby_cil", v)} />
        <TextField label="Teď řešíme" value={data.trenink_aktualni_cil || ""} editing={editing} multiline placeholder="Co řešíme tento měsíc / blok" onSave={(v) => saveField("trenink_aktualni_cil", v)} />
        <TextField label="Kdy jí vyhovují tréninky" value={data.zaklad_vyhovuje_doba || ""} editing={editing} placeholder="Např. dopoledne, večer, podle týdne" onSave={(v) => saveField("zaklad_vyhovuje_doba", v)} />
        <TextField label="Omezení v tréninku" value={data.trenink_omezeni || ""} editing={editing} multiline placeholder="Bolesti, mobilita, výbušnost…" onSave={(v) => saveField("trenink_omezeni", v)} />
      </Podsekce>

      <Podsekce
        id="cviky"
        icon={Dumbbell}
        title="Cviky a váhy"
        defaultOpen
        badge={cvikyPocet ? <PocetOdznak>{cvikyPocet}</PocetOdznak> : <PrazdnyOdznak>žádné</PrazdnyOdznak>}
      >
        <ExerciseList
          rows={data.cviky || []}
          editing={editing}
          variant="cviky"
          accent="violet"
          onChange={(next) => saveField("cviky", next)}
        />
      </Podsekce>

      <Podsekce
        id="kardio"
        icon={Heart}
        title="Kardio"
        badge={kardioPocet ? <PocetOdznak>{kardioPocet}</PocetOdznak> : <PrazdnyOdznak>žádné</PrazdnyOdznak>}
      >
        <ExerciseList
          rows={data.kardio || []}
          editing={editing}
          variant="kardio"
          accent="rose"
          onChange={(next) => saveField("kardio", next)}
        />
      </Podsekce>

      <Podsekce
        id="zapisy"
        icon={CalendarDays}
        title="Zápisy z tréninků"
        badge={zapisyPocet ? <PocetOdznak>{zapisyPocet}</PocetOdznak> : null}
      >
        <TrainingSessionsLog klientName={klientName} canEdit={canEdit} onCount={setZapisyPocet} />
        {data.posledni_treninky && (
          <details className="mt-3 text-xs text-slate-500">
            <summary className="cursor-pointer font-bold hover:text-slate-700">Starý log (před přechodem na strukturované záznamy)</summary>
            <pre className="mt-2 max-h-64 overflow-y-auto whitespace-pre-wrap break-words rounded-xl border border-slate-200 bg-slate-50 p-2.5 text-[11px]">{data.posledni_treninky}</pre>
          </details>
        )}
      </Podsekce>

      <Podsekce
        id="poznamky"
        icon={BookOpen}
        title="Poznámky"
        badge={String(data.poznamky || "").trim() ? null : <PrazdnyOdznak>nevyplněno</PrazdnyOdznak>}
      >
        <TextField value={data.poznamky || ""} editing={editing} multiline placeholder="Cokoli důležitého, co se trenérovi hodí" onSave={(v) => saveField("poznamky", v)} />
      </Podsekce>

      <Podsekce
        id="nahrady"
        icon={Users}
        title="Pro zaskakujícího trenéra"
        badge={String(data.pro_nahrady || "").trim() ? null : <PrazdnyOdznak>nevyplněno</PrazdnyOdznak>}
      >
        <TextField value={data.pro_nahrady || ""} editing={editing} multiline placeholder="Klepni a napiš, co má vědět kolega, který tě zaskočí." onSave={(v) => saveField("pro_nahrady", v)} />
      </Podsekce>
    </div>
  );
}

// === Pomocné komponenty ===


// 9.9.2026 — rozbalovaci podsekce profilu. Stav rozbaleni se drzi v localStorage,
// at trener po navratu na klienta vidi to, co si otevrel naposledy.
const OTEVRENE_KLIC = "fw40_tp_otevrene";
function nactiOtevrene() {
  if (typeof window === "undefined") return {};
  try { return JSON.parse(window.localStorage.getItem(OTEVRENE_KLIC) || "{}") || {}; } catch { return {}; }
}
function ulozOtevrene(mapa) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(OTEVRENE_KLIC, JSON.stringify(mapa)); } catch { /* quota */ }
}

function PocetOdznak({ children, tone }) {
  const barvy = tone === "rose" ? "bg-rose-100 text-rose-800" : "bg-brand-100 text-brand-800";
  return <span className={`inline-flex min-w-[24px] items-center justify-center rounded-full px-2 py-0.5 text-xs font-bold tabular-nums ${barvy}`}>{children}</span>;
}

function PrazdnyOdznak({ children }) {
  return <span className="text-[13px] italic text-slate-400">{children}</span>;
}

function Podsekce({ id, icon: Icon, title, badge, danger = false, defaultOpen = false, children }) {
  const [open, setOpen] = useState(() => {
    const m = nactiOtevrene();
    return typeof m[id] === "boolean" ? m[id] : defaultOpen;
  });
  const prepni = () => {
    setOpen((o) => {
      const novy = !o;
      const m = nactiOtevrene();
      m[id] = novy;
      ulozOtevrene(m);
      return novy;
    });
  };
  return (
    <div className={`overflow-hidden rounded-2xl border ${danger ? "border-rose-200" : "border-slate-200"} bg-white`}>
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={prepni}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); prepni(); } }}
        className={`flex min-h-[56px] cursor-pointer select-none items-center justify-between gap-2.5 px-3.5 py-3 ${danger ? "bg-rose-50" : "bg-white"}`}
      >
        <div className="flex min-w-0 items-center gap-2.5">
          {Icon && <Icon className={`h-4 w-4 shrink-0 ${danger ? "text-rose-600" : "text-brand-700"}`} />}
          <div className={`truncate text-[14.5px] font-bold ${danger ? "text-rose-900" : "text-slate-900"}`}>{title}</div>
          {badge}
        </div>
        <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${open ? "" : "-rotate-90"} ${danger ? "text-rose-400" : "text-slate-400"}`} />
      </div>
      {open && (
        <div className="space-y-3 border-t border-slate-100 bg-slate-50/40 px-3.5 py-3">
          {children}
        </div>
      )}
    </div>
  );
}

// TextField — view (read-only zobrazení) + edit (textarea auto-save on blur).
function TextField({ label, value, editing, multiline = false, rows = 3, placeholder = "", onSave }) {
  const [draft, setDraft] = useState(value || "");
  const taRef = useRef(null);
  useEffect(() => { setDraft(value || ""); }, [value]);

  // Auto-grow: textarea se roztáhne podle obsahu, aby byl celý text vidět bez
  // scrollování uvnitř malého okénka (mobil). Min. výška = rows řádků.
  const autoGrow = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.max(el.scrollHeight, rows * 24 + 16)}px`;
  };
  useEffect(() => { if (editing && multiline) autoGrow(taRef.current); }, [editing, multiline, draft]);

  const handleBlur = () => {
    if (draft !== value) onSave(draft);
  };

  if (!editing) {
    // Read-only: zobrazí buď hodnotu nebo italic placeholder
    return (
      <div>
        {label && <div className="mb-1.5 rounded-xl bg-brand-100 px-3 py-1.5 text-[14px] font-extrabold leading-tight tracking-tight text-brand-800">{label}</div>}
        {value ? (
          <div className="text-sm sm:text-base text-slate-800 whitespace-pre-wrap break-words leading-relaxed">{value}</div>
        ) : (
          <div className="text-sm text-slate-400 italic">{placeholder || "Nevyplněno"}</div>
        )}
      </div>
    );
  }

  return (
    <div>
      {label && <div className="mb-1.5 rounded-xl bg-brand-100 px-3 py-1.5 text-[14px] font-extrabold leading-tight tracking-tight text-brand-800">{label}</div>}
      {multiline ? (
        <textarea
          ref={taRef}
          value={draft}
          onChange={(e) => { setDraft(e.target.value); autoGrow(e.target); }}
          onBlur={handleBlur}
          rows={rows}
          placeholder={placeholder}
          className={`w-full rounded-xl border-2 bg-white px-3 py-2 text-base leading-relaxed outline-none resize-none overflow-hidden focus:border-brand-400 ${draft ? "border-slate-200" : "border-dashed border-slate-300"}`}
        />
      ) : (
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={`w-full rounded-xl border-2 bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 sm:text-base ${draft ? "border-slate-200" : "border-dashed border-slate-300"}`}
        />
      )}
    </div>
  );
}
// === ExerciseList - karta-based UI místo tabulky ===
// Designové cíle: jednoduchost, mobile-first, velké tap targety.
// View mode: kompaktní karty s názvem cviku + meta (váha/info + poznámka).
// Edit mode: per-karta inline editor s 3 fieldy + smazat. Per-field save on blur.
// Picker: bottom sheet (mobile) / centered modal (desktop) s vyhledáváním 169 cviků z DB.
function ExerciseList({ rows, editing, variant, accent = "violet", onChange }) {
  // variant "cviky" = { cvik, vaha, poznamka }
  // variant "kardio" = { cvik, info, poznamka }
  const valueKey = variant === "kardio" ? "info" : "vaha";
  const valueLabel = variant === "kardio" ? "Informace" : "Váha (kg)";
  const valuePlaceholder = variant === "kardio" ? "20s/40s × 8, 2 kola…" : "16, 10kg, malá červená…";
  const accentMap = {
    violet: { ring: "ring-brand-300", text: "text-brand-700", bg: "bg-brand-100", border: "border-brand-200", icon: Dumbbell },
    rose: { ring: "ring-rose-300", text: "text-rose-700", bg: "bg-rose-100", border: "border-rose-200", icon: Heart },
  };
  const a = accentMap[accent] || accentMap.violet;
  const Icon = a.icon;

  const [localRows, setLocalRows] = useState(rows || []);
  const [openEditIdx, setOpenEditIdx] = useState(null); // index expandované karty pro edit
  const [pickerOpen, setPickerOpen] = useState(false);
  const [vseZobrazeno, setVseZobrazeno] = useState(false);
  const [pickerForIdx, setPickerForIdx] = useState(null); // null = nový cvik, číslo = edit existujícího
  const debounceRef = useRef(null);
  useEffect(() => { setLocalRows(rows || []); }, [rows]);

  const commit = (next, immediate = false) => {
    setLocalRows(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (immediate) onChange(next);
    else debounceRef.current = setTimeout(() => onChange(next), 350);
  };

  const handlePickCvik = (cvik) => {
    // cvik = { id, nazev, ... } z DB, nebo { id: null, nazev: "custom string" } pro vlastní.
    // 29.5.2026 v10: vždy nová karta s vybraným jménem (žádné prázdné "Bez názvu" karty).
    if (pickerForIdx === null) {
      const newRow = { cvik: cvik.nazev, cvikId: cvik.id || null, [valueKey]: "", poznamka: "" };
      const next = [...localRows, newRow];
      commit(next, true);
      // Auto-expand novou kartu aby trenér mohl hned doplnit váhu + poznámku.
      setOpenEditIdx(next.length - 1);
    } else {
      // Update jména existující karty
      const next = localRows.map((r, i) => i === pickerForIdx ? { ...r, cvik: cvik.nazev, cvikId: cvik.id || null } : r);
      commit(next, true);
    }
    setPickerOpen(false);
    setPickerForIdx(null);
  };

  const updateField = (idx, key, val) => {
    const next = localRows.map((r, i) => i === idx ? { ...r, [key]: val } : r);
    commit(next);
  };
  const removeRow = (idx) => {
    commit(localRows.filter((_, i) => i !== idx), true);
    if (openEditIdx === idx) setOpenEditIdx(null);
  };

  // 29.5.2026 v10: zobrazujeme jen řádky s vyplněným cvikem.
  // Prázdné karty by mohly vzniknout z historických dat (předchozí verze umožňovala addRow bez pickeru).
  // Cleanup je defenzivní - skryje + když user uloží něco jiného, prázdné se commitnou pryč.
  const visibleRows = localRows
    .map((r, i) => ({ ...r, __origIdx: i }))
    .filter((r) => (r.cvik && r.cvik.trim()) || r.__origIdx === openEditIdx);

  // 9.9.2026 — na telefonu je patnact cviku dlouhy vypis, tak ukazeme prvnich sest
  // a zbytek na klepnuti. Pri otevrene karte se seznam nesbaluje.
  const omezit = !vseZobrazeno && openEditIdx === null && visibleRows.length > 7;
  const zobrazeneRadky = omezit ? visibleRows.slice(0, 6) : visibleRows;

  return (
    <div className="space-y-2">
      {visibleRows.length === 0 && !editing && (
        <div className="text-sm text-slate-400 italic">Žádné cviky.</div>
      )}
      {zobrazeneRadky.map((r) => {
        const idx = r.__origIdx;
        const isOpen = editing && openEditIdx === idx;
        const fromDb = !!r.cvikId;
        // 9.9.2026 — kilogramy jednotne. Trener napise 70, 4kg i 12,5 kg, do dlazdice
        // vytahneme jen cislo a jednotku dopiseme sami. Kdyz v hodnote cislo neni
        // (napr. "mala cervena"), zustane ikona a text se ukaze v radku pod nazvem.
        const hodnota = String(r[valueKey] || "").trim();
        const shodaKg = variant === "cviky" ? hodnota.match(/^(\d+(?:[.,]\d+)?)\s*(?:kg)?$/i) : null;
        const cislo = shodaKg ? shodaKg[1] : "";
        const kratkaHodnota = !cislo && hodnota && hodnota.length <= 4 ? hodnota : "";
        return (
          <div key={idx} className={`bg-white rounded-xl border ${a.border} shadow-sm overflow-hidden transition-shadow ${isOpen ? `ring-2 ${a.ring}` : ""}`}>
            {/* Kompaktní karta - vždy viditelná, tap pro otevření v edit mode */}
            <button
              type="button"
              onClick={() => editing && setOpenEditIdx(isOpen ? null : idx)}
              disabled={!editing}
              className={`w-full text-left px-3 sm:px-4 py-3 flex items-center gap-3 ${editing ? "hover:bg-slate-50/50 active:bg-slate-100/50" : "cursor-default"}`}
            >
              {/* 9.9.2026 — v dlazdici je rovnou vaha, at ji trener vidi bez cteni druheho radku.
                  Kdyz je hodnota dlouha nebo zadna, zustava ikona jako driv. */}
              <div className={`flex h-11 w-11 flex-none flex-col items-center justify-center rounded-xl sm:h-12 sm:w-12 ${a.bg} ${a.text}`}>
                {cislo || kratkaHodnota ? (
                  <>
                    <span className={`font-extrabold leading-none ${(cislo || kratkaHodnota).length > 4 ? "text-[13px]" : "text-[16px]"}`}>{cislo || kratkaHodnota}</span>
                    {cislo && <span className="mt-0.5 text-[11px] font-bold leading-none opacity-70">kg</span>}
                  </>
                ) : fromDb ? <Sparkles className="h-4 w-4 sm:h-5 sm:w-5" /> : <Icon className="h-4 w-4 sm:h-5 sm:w-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm sm:text-base font-bold text-slate-900 truncate">
                  {r.cvik}
                </div>
                {/* Druhý řádek zobrazíme jen pokud má co ukázat - prázdný řádek působí čistěji než "Bez detailu". */}
                {[cislo ? "" : r[valueKey], r.poznamka].filter(Boolean).length > 0 && (
                  <div className="mt-0.5 truncate text-xs text-slate-500 sm:text-[13px]">
                    {[cislo ? "" : r[valueKey], r.poznamka].filter(Boolean).join(", ")}
                  </div>
                )}
              </div>
              {editing && (
                <ChevronRight className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} />
              )}
            </button>

            {/* Expandovaný editor */}
            {isOpen && (
              <div className="px-3 sm:px-4 pb-3 sm:pb-4 border-t border-slate-100 bg-slate-50/50 space-y-3 pt-3">
                {/* Cvik - tap otevírá picker, needitovatelný inline */}
                <div>
                  <div className="mb-1 text-xs font-bold text-slate-600">Cvik</div>
                  <button
                    type="button"
                    onClick={() => { setPickerForIdx(idx); setPickerOpen(true); }}
                    className="w-full text-left px-3 py-2.5 rounded-xl border border-slate-200 bg-white hover:border-brand-300 hover:bg-brand-50/30 transition-colors flex items-center gap-2"
                  >
                    {fromDb && <Sparkles className="w-4 h-4 text-brand-600 flex-shrink-0" />}
                    <span className="flex-1 text-sm font-semibold text-slate-800 truncate">
                      {r.cvik || <span className="text-slate-400 italic font-normal">Vyber cvik z databáze</span>}
                    </span>
                    <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  </button>
                </div>

                {/* Váha / Info */}
                <div>
                  <div className="mb-1 text-xs font-bold text-slate-600">{valueLabel}</div>
                  <input
                    type="text"
                    value={r[valueKey] || ""}
                    onChange={(e) => updateField(idx, valueKey, e.target.value)}
                    onBlur={() => onChange(localRows)}
                    placeholder={valuePlaceholder}
                    className="field w-full sm:text-sm"
                  />
                </div>

                {/* Poznámka */}
                <div>
                  <div className="mb-1 text-xs font-bold text-slate-600">Poznámka</div>
                  <input
                    type="text"
                    value={r.poznamka || ""}
                    onChange={(e) => updateField(idx, "poznamka", e.target.value)}
                    onBlur={() => onChange(localRows)}
                    placeholder="12×3, technika, bedna…"
                    className="field w-full sm:text-sm"
                  />
                </div>

                {/* Akce */}
                <div className="flex items-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setOpenEditIdx(null)}
                    className="btn btn-primary flex-1"
                  >
                    <Check className="w-4 h-4" /> Hotovo
                  </button>
                  <button
                    type="button"
                    onClick={() => removeRow(idx)}
                    title="Smazat cvik"
                    className="px-3 py-2.5 rounded-xl bg-white border border-rose-200 text-rose-600 hover:bg-rose-50 inline-flex items-center justify-center"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {omezit && (
        <button
          type="button"
          onClick={() => setVseZobrazeno(true)}
          className="flex min-h-[44px] w-full items-center justify-center rounded-xl border border-slate-200 bg-white text-[13.5px] font-semibold text-brand-deep"
        >
          Zobrazit všech {visibleRows.length} {variant === "kardio" ? "položek" : "cviků"}
        </button>
      )}

      {editing && (
        <button
          type="button"
          onClick={() => { setPickerForIdx(null); setPickerOpen(true); }}
          className="w-full inline-flex items-center justify-center gap-2 text-base font-bold px-4 py-3 sm:py-3 border-2 border-dashed border-slate-300 hover:border-brand-400 hover:bg-brand-50 rounded-xl text-slate-600 hover:text-brand-700 transition-colors"
        >
          <Plus className="w-5 h-5" /> Přidat cvik
        </button>
      )}

      <CvikPickerSheet
        open={pickerOpen}
        variant={variant}
        onPick={handlePickCvik}
        onClose={() => { setPickerOpen(false); setPickerForIdx(null); }}
      />
    </div>
  );
}

// === CvikPickerSheet - mobile bottom sheet / desktop centered modal ===
// Velký tap target na cvik, instant search (lokálně), návrhy pre-filterované podle variant
// (cviky = vše kromě Kardio + Mobilita + Rehabilitace; kardio = jen Kardio kategorie).
// Když user napíše custom name (žádný DB match): nabídka "Použít vlastní" jako amber button dole.
function CvikPickerSheet({ open, variant, onPick, onClose }) {
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery("");
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // ESC close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const filtered = useMemo(() => {
    let pool = CVIKY_DATABAZE;
    // Filtruj podle variantu
    if (variant === "kardio") {
      pool = pool.filter((c) => c.kategorie === "Kardio");
    } else {
      // "cviky" = vše krom kardio (mobilita + rehab necháme, jsou užitečné při tréninku)
      pool = pool.filter((c) => c.kategorie !== "Kardio");
    }
    if (!query.trim()) return pool.slice(0, 40);
    return searchCviky(query, 60).filter((c) =>
      variant === "kardio" ? c.kategorie === "Kardio" : c.kategorie !== "Kardio"
    );
  }, [query, variant]);

  // Zda query přesně sedí na nějaký existující cvik (z DB)
  const exactMatch = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return CVIKY_DATABAZE.find((c) => c.nazev.toLowerCase() === q) || null;
  }, [query]);

  if (!open) return null;

  return createPortal(
    <div
      className="nad-klavesnici fixed inset-0 z-50 bg-brand-dark/50 backdrop-blur-sm flex items-end sm:items-center justify-center animate-in fade-in duration-150"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col max-h-[85vh] sm:max-h-[80vh] animate-in slide-in-from-bottom sm:zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle (mobile) */}
        <div className="sm:hidden flex justify-center pt-2 pb-1">
          <div className="w-10 h-1 bg-slate-300 rounded-full" />
        </div>

        {/* Header */}
        <div className="px-4 pt-2 sm:pt-4 pb-3 border-b border-slate-100 flex items-center justify-between gap-2">
          <h3 className="text-lg font-bold text-slate-900">
            {variant === "kardio" ? "Vyber kardio" : "Vyber cvik"}
          </h3>
          <button onClick={onClose} className="p-2 -mr-1 rounded-xl hover:bg-slate-100 min-h-[44px] sm:min-h-0">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b border-slate-100">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={variant === "kardio" ? "Veslo, Skierg, Airbike…" : "Dřep, Mrtvý tah, Bench…"}
              className="field w-full pl-9 pr-9 sm:text-sm"
            />
            {query && (
              <button
                type="button"
                onClick={() => { setQuery(""); inputRef.current?.focus(); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100"
              >
                <X className="w-4 h-4 text-slate-400" />
              </button>
            )}
          </div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mt-2 px-1">
            {filtered.length} {filtered.length === 1 ? "cvik" : filtered.length < 5 ? "cviky" : "cviků"}
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto py-1">
          {filtered.length === 0 && (
            <div className="px-4 py-8 text-sm text-slate-400 italic text-center">
              Žádné cviky nenalezeny pro „{query}"
            </div>
          )}
          {filtered.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onPick(c)}
              className="w-full text-left px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-brand-50/40 active:bg-brand-100/60 transition-colors"
            >
              <div className="flex items-start gap-2.5">
                <Sparkles className="w-4 h-4 text-brand-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm sm:text-base font-semibold text-slate-900 break-words">{c.nazev}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                    <span className="font-medium">{c.kategorie}</span>
                    <Oddelovac />
                    <span>{c.vybaveni}</span>
                    {c.obtiznost && (
                      <>
                        <Oddelovac />
                        <span title={`Obtížnost ${c.obtiznost}/3`} className="text-amber-600 tracking-tighter">
                          {"●".repeat(c.obtiznost)}{"○".repeat(3 - c.obtiznost)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Custom cvik footer - jen pokud query je vyplněné a neexistuje exact match */}
        {query.trim() && !exactMatch && (
          <button
            type="button"
            onClick={() => onPick({ id: null, nazev: query.trim() })}
            className="px-4 py-3 bg-amber-50 hover:bg-amber-100 active:bg-amber-200 border-t border-amber-200 text-left transition-colors"
          >
            <div className="text-sm font-bold text-amber-800 inline-flex items-center gap-1.5">
              <Plus className="w-4 h-4" /> Použít vlastní cvik: „{query.trim()}"
            </div>
            <div className="text-[11px] text-amber-700 mt-0.5">Nepřidá se do centrální databáze (nezahrne se do rollupu).</div>
          </button>
        )}
      </div>
    </div>,
    document.body
  );
}
